import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiClientError, getHealth, type HealthResponse } from '../lib/api';

export type ServerStatus =
  | 'online'
  | 'checking'
  | 'unreachable'
  | 'health_failed'
  | 'waiting_backend'
  | 'proxy_unavailable'
  | 'restarting';

export type HealthDiagnosis =
  | 'online'
  | 'server_unreachable'
  | 'health_endpoint_failed'
  | 'waiting_for_backend'
  | 'proxy_unavailable'
  | 'backend_restarting'
  | 'checking';

export interface HealthState {
  status: ServerStatus;
  diagnosis: HealthDiagnosis;
  label: string;
  detail: string;
  health: HealthResponse | null;
  retryInSec: number | null;
  refresh: () => void;
}

const RETRY_BASE_MS = 3000;
const RETRY_MAX_MS = 15000;

function labelFor(d: HealthDiagnosis, retryInSec: number | null): string {
  switch (d) {
    case 'online':
      return 'Online';
    case 'checking':
      return 'Checking…';
    case 'server_unreachable':
      return 'Server unreachable';
    case 'health_endpoint_failed':
      return 'Health endpoint failed';
    case 'waiting_for_backend':
      return 'Waiting for backend';
    case 'proxy_unavailable':
      return 'Proxy unavailable';
    case 'backend_restarting':
      return 'Backend restarting';
    default:
      return retryInSec != null
        ? `Attempting reconnect… Retry in ${retryInSec}s`
        : 'Attempting reconnect…';
  }
}

function detailFor(d: HealthDiagnosis): string {
  switch (d) {
    case 'online':
      return 'API health check succeeded.';
    case 'checking':
      return 'Contacting /api/health…';
    case 'server_unreachable':
      return 'Could not reach the Vite proxy or backend (network error).';
    case 'health_endpoint_failed':
      return 'The health endpoint responded with an error.';
    case 'waiting_for_backend':
      return 'Frontend is up; backend has not answered yet.';
    case 'proxy_unavailable':
      return 'Vite may be running but /api proxy is not forwarding.';
    case 'backend_restarting':
      return 'Backend was online and dropped — likely restarting.';
    default:
      return 'Attempting reconnect…';
  }
}

/**
 * Polls /api/health with clear offline diagnoses and automatic backoff retry.
 */
export function useHealth(pollMs = 20000, enabled = true): HealthState {
  const [status, setStatus] = useState<ServerStatus>('checking');
  const [diagnosis, setDiagnosis] = useState<HealthDiagnosis>('checking');
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [retryInSec, setRetryInSec] = useState<number | null>(null);
  const wasOnline = useRef(false);
  const failStreak = useRef(0);
  const timer = useRef<number | null>(null);
  const retryTimer = useRef<number | null>(null);
  const countdownTimer = useRef<number | null>(null);

  const clearTimers = (): void => {
    if (timer.current !== null) window.clearInterval(timer.current);
    if (retryTimer.current !== null) window.clearTimeout(retryTimer.current);
    if (countdownTimer.current !== null) {
      window.clearInterval(countdownTimer.current);
    }
    timer.current = null;
    retryTimer.current = null;
    countdownTimer.current = null;
  };

  const scheduleRetry = useCallback((check: () => void): void => {
    const delay = Math.min(
      RETRY_MAX_MS,
      RETRY_BASE_MS * Math.max(1, failStreak.current),
    );
    let left = Math.ceil(delay / 1000);
    setRetryInSec(left);
    if (countdownTimer.current !== null) {
      window.clearInterval(countdownTimer.current);
    }
    countdownTimer.current = window.setInterval(() => {
      left -= 1;
      setRetryInSec(left > 0 ? left : null);
      if (left <= 0 && countdownTimer.current !== null) {
        window.clearInterval(countdownTimer.current);
        countdownTimer.current = null;
      }
    }, 1000);
    if (retryTimer.current !== null) window.clearTimeout(retryTimer.current);
    retryTimer.current = window.setTimeout(() => {
      setRetryInSec(null);
      check();
    }, delay);
  }, []);

  const check = useCallback(async () => {
    try {
      const h = await getHealth();
      setHealth(h);
      failStreak.current = 0;
      setRetryInSec(null);
      if (h.database === 'connected') {
        wasOnline.current = true;
        setStatus('online');
        setDiagnosis('online');
      } else {
        setStatus('health_failed');
        setDiagnosis('health_endpoint_failed');
        scheduleRetry(() => void check());
      }
    } catch (err) {
      failStreak.current += 1;
      setHealth(null);

      if (wasOnline.current) {
        setStatus('restarting');
        setDiagnosis('backend_restarting');
      } else if (err instanceof ApiClientError) {
        if (err.offline || err.code === 'offline') {
          setStatus('unreachable');
          setDiagnosis('server_unreachable');
        } else if (err.status >= 500 || err.status === 502 || err.status === 504) {
          setStatus('proxy_unavailable');
          setDiagnosis('proxy_unavailable');
        } else if (err.status === 0) {
          setStatus('waiting_backend');
          setDiagnosis('waiting_for_backend');
        } else {
          setStatus('health_failed');
          setDiagnosis('health_endpoint_failed');
        }
      } else {
        setStatus('unreachable');
        setDiagnosis('server_unreachable');
      }
      scheduleRetry(() => void check());
    }
  }, [scheduleRetry]);

  useEffect(() => {
    if (!enabled) return undefined;
    void check();
    timer.current = window.setInterval(() => void check(), pollMs);
    return () => clearTimers();
  }, [check, enabled, pollMs]);

  const d =
    diagnosis === 'online' || diagnosis === 'checking'
      ? diagnosis
      : retryInSec != null
        ? diagnosis
        : diagnosis;

  return {
    status,
    diagnosis: d,
    label:
      diagnosis !== 'online' && diagnosis !== 'checking' && retryInSec != null
        ? `${labelFor(diagnosis, null)} · Retry in ${retryInSec}s`
        : labelFor(diagnosis, retryInSec),
    detail: detailFor(diagnosis),
    health,
    retryInSec,
    refresh: () => void check(),
  };
}
